const DEFAULT_FILE_RULES = {
    formats: ["pdf", "docx", "xlsx", "jpg", "png"],
    max_files: 5,
    max_size_mb: 20,
};

const questionId = () => `question-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const textValue = (value, fallback = "") => {
    if (["string", "number", "boolean"].includes(typeof value)) return String(value);
    return fallback;
};

const hasVisibleQuestionText = (value) => String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;|&#160;/gi, " ")
    .trim().length > 0;

export const normalizeQuestions = (list) =>
    (Array.isArray(list) ? list : []).map((raw, index) => {
        const q = raw && typeof raw === "object" ? raw : {};
        const rawOptions = Array.isArray(q.options)
            ? q.options
            : Array.isArray(q.answers) ? q.answers : [];
        const options = rawOptions.map((option) => String(
            option && typeof option === "object"
                ? option.text ?? option.value ?? option.label ?? ""
                : option ?? ""
        ));
        const answerType = ["single", "multiple", "short_text", "long_text", "file"].includes(q.answer_type)
            ? q.answer_type
            : options.length ? "single" : "short_text";
        let correctIndexes = Array.isArray(q.correct_option_indexes)
            ? q.correct_option_indexes.map(Number).filter(Number.isFinite)
            : q.correct_option_index != null ? [Number(q.correct_option_index)]
                : q.correct_answer_index != null ? [Number(q.correct_answer_index)] : [];
        if (!correctIndexes.length && q.correct_answer != null && options.length) {
            const matchingIndex = typeof q.correct_answer === "number"
                ? Number(q.correct_answer)
                : options.findIndex((option) => option === String(q.correct_answer));
            if (matchingIndex >= 0) correctIndexes = [matchingIndex];
        }
        correctIndexes = correctIndexes
            .filter((value) => value >= 0 && value < options.length)
            .slice(0, answerType === "single" ? 1 : Number.MAX_SAFE_INTEGER);
        const evaluationMode = ["none", "auto", "manual"].includes(q.evaluation_mode)
            ? q.evaluation_mode
            : correctIndexes.length && ["single", "multiple"].includes(answerType) ? "auto" : "none";

        return {
            id: q.id,
            client_id: q.client_id || (q.id ? `db-${q.id}` : `${questionId()}-${index}`),
            question_text: String(q.question_text || q.question || q.text || ""),
            explanation: String(q.explanation || ""),
            answer_type: answerType,
            evaluation_mode: evaluationMode,
            required: Boolean(q.required),
            track_time: Boolean(q.track_time),
            options,
            allow_custom_answer: ["single", "multiple"].includes(answerType) && Boolean(q.allow_custom_answer),
            custom_answer_label: String(q.custom_answer_label || "Другой вариант"),
            correct_option_indexes: correctIndexes,
            correct_option_index: answerType === "single" && correctIndexes.length ? correctIndexes[0] : null,
            max_score: q.max_score != null ? String(q.max_score) : evaluationMode === "auto" ? "1" : "",
            evaluation_criteria: String(q.evaluation_criteria || ""),
            file_rules: {
                formats: Array.isArray(q.file_rules?.formats) ? q.file_rules.formats : DEFAULT_FILE_RULES.formats,
                max_files: Number(q.file_rules?.max_files || DEFAULT_FILE_RULES.max_files),
                max_size_mb: Number(q.file_rules?.max_size_mb || DEFAULT_FILE_RULES.max_size_mb),
            },
            attachments: Array.isArray(q.attachments) ? q.attachments : [],
        };
    });

export const newQuestion = () => normalizeQuestions([{}])[0];

export const questionsPayload = (list) =>
    normalizeQuestions(list).map((q) => {
        const optionsWithIndexes = q.options
            .map((option, originalIndex) => ({ value: option.trim(), originalIndex }))
            .filter((option) => option.value);
        const correctOptionIndex = q.correct_option_index == null
            ? null
            : optionsWithIndexes.findIndex((option) => option.originalIndex === q.correct_option_index);
        return {
            text: q.question_text.trim(),
            options: optionsWithIndexes.length ? optionsWithIndexes.map((option) => option.value) : null,
            correct_option_index: correctOptionIndex >= 0 ? correctOptionIndex : null,
        };
    }).filter((q) => q.text);

export const emptyFormData = (initialData = {}) => {
    const source = initialData && typeof initialData === "object" && !Array.isArray(initialData)
        ? initialData
        : {};
    return {
        name: textValue(source.name),
        test_type: ["url", "Вопросно-ответная форма"].includes(source.test_type)
            ? source.test_type
            : "Вопросно-ответная форма",
        results_type: textValue(source.results_type, "текстовый результат"),
        url: textValue(source.url),
        description: textValue(source.description),
        instruction_text: textValue(source.instruction_text),
        duration_minutes: source.duration_minutes != null
            ? textValue(source.duration_minutes)
            : "",
        passing_score_percent: source.passing_score_percent != null
            ? textValue(source.passing_score_percent)
            : "80",
        track_total_time: source.track_total_time === true || source.track_total_time === "true",
        strict_integrity: source.strict_integrity === true || source.strict_integrity === "true",
        integrity_max_away_seconds: source.integrity_max_away_seconds != null
            ? textValue(source.integrity_max_away_seconds)
            : "300",
        integrity_max_tab_switches: source.integrity_max_tab_switches != null
            ? textValue(source.integrity_max_tab_switches)
            : "10",
        integrity_on_violation: ["fail", "pass_flagged", "hr_review"].includes(source.integrity_on_violation)
            ? source.integrity_on_violation
            : "",
        suspicious_is_violation: source.suspicious_is_violation === true || source.suspicious_is_violation === "true",
        success_rule: source.success_rule === "hr_decision" ? "hr_decision" : "score",
        test_category: source.test_category === "universal" ? "universal" : "professional",
        consent_document: source.consent_document && typeof source.consent_document === "object"
            ? source.consent_document
            : null,
    };
};

export const validateTestEditor = (formData, questions) => {
    const errors = [];
    if (!String(formData?.name || "").trim()) errors.push("Укажите название теста.");
    if (!String(formData?.instruction_text || "").trim()) errors.push("Добавьте инструкцию для кандидата.");
    const passingPercent = Number(formData?.passing_score_percent);
    if (!Number.isInteger(passingPercent) || passingPercent < 1 || passingPercent > 100) {
        errors.push("Проходной результат должен быть целым числом от 1 до 100 процентов.");
    }
    if (formData?.test_type === "url") {
        try {
            const url = new URL(String(formData.url || ""));
            if (!["http:", "https:"].includes(url.protocol)) throw new Error();
        } catch {
            errors.push("Укажите корректную ссылку на внешний тест.");
        }
    }
    if (formData?.test_type === "Вопросно-ответная форма") {
        const duration = String(formData?.duration_minutes || "").trim();
        if (duration && (!Number.isInteger(Number(duration)) || Number(duration) < 1 || Number(duration) > 600)) {
            errors.push("Лимит времени должен быть целым числом от 1 до 600 минут.");
        }
        if (!questions.length) errors.push("Добавьте хотя бы один вопрос.");
        questions.forEach((question, index) => {
            if (!hasVisibleQuestionText(question?.question_text)) errors.push(`Заполните текст вопроса №${index + 1}.`);
            const options = Array.isArray(question?.options) ? question.options : [];
            if (["single", "multiple"].includes(question?.answer_type) && options.length < 2) {
                errors.push(`Для вопроса №${index + 1} добавьте минимум два варианта ответа.`);
            }
            if (["single", "multiple"].includes(question?.answer_type) && options.some((option) => !String(option || "").trim())) {
                errors.push(`Заполните или удалите пустые варианты в вопросе №${index + 1}.`);
            }
            if (question?.allow_custom_answer && !String(question?.custom_answer_label || "").trim()) {
                errors.push(`Укажите подпись для своего варианта ответа в вопросе №${index + 1}.`);
            }
            if (question?.evaluation_mode === "auto" && !(question?.correct_option_indexes || []).length) {
                errors.push(`Укажите правильный ответ в вопросе №${index + 1}.`);
            }
            if (question?.evaluation_mode !== "none" && !(Number(question?.max_score) > 0)) {
                errors.push(`Укажите максимальный балл в вопросе №${index + 1}.`);
            }
        });
    }
    return errors;
};

export const normalizeGeneratedTest = (payload, currentForm = {}) => {
    if (!payload || typeof payload !== "object") throw new Error("AI вернул пустой ответ");
    const questions = normalizeQuestions(payload.questions);
    if (!questions.length) throw new Error("AI не сформировал вопросы. Попробуйте изменить тему и повторить генерацию");
    return {
        formData: {
            ...emptyFormData(currentForm),
            name: String(payload.name || currentForm.name || "Сгенерированный тест"),
            test_type: "Вопросно-ответная форма",
            results_type: "текстовый результат",
            url: "",
            description: String(payload.description || ""),
            instruction_text: String(payload.instruction || payload.instruction_text || "Ответьте на вопросы теста"),
        },
        questions,
    };
};

